import React from 'react';
import { Typography, Paper, Box, Grid, Alert } from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { Dayjs } from 'dayjs';
import { SaveIndicator, FormNumericTextField, FormTextField, FormSelect, FormSwitch, FormTimePicker } from '@personal-dashboard/ui';
import { useDayMetricsForm } from '../../hooks/useDayMetricsForm';

interface DayMetricsFormProps {
  selectedDate: Dayjs;
}

const DayMetricsForm: React.FC<DayMetricsFormProps> = ({ selectedDate }) => {
  const { loading, error, methods, status, sections } = useDayMetricsForm(selectedDate);

  if (loading) return null;
  if (error) return <Alert severity="error">Failed to load metrics: {error.message}</Alert>;

  return (
    <FormProvider {...methods}>
      <Paper sx={{ p: 3, backgroundColor: 'background.paper' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Daily Metrics
          </Typography>
          <SaveIndicator status={status} />
        </Box>

        {sections.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No custom fields configured. Please configure them in Settings.
          </Typography>
        ) : (
          <Grid container spacing={4}>
            {sections.map((section: any) => (
              <Grid item xs={12} md={6} key={section.id}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 2, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}>
                    {section.title}
                  </Typography>

                  <Grid container spacing={3}>
                    {(section.fields || []).filter((f: any) => f.published !== false).map((field: any) => (
                      <Grid item xs={12} key={field.id}>
                        {field.type === 'NUMBER' && (
                          <FormNumericTextField
                            name={field.id}
                            label={field.name}
                            fullWidth
                            size="small"
                            allowDecimal
                          />
                        )}
                        {field.type === 'TEXT' && (
                          <FormTextField
                            name={field.id}
                            label={field.name}
                            fullWidth
                            size="small"
                          />
                        )}
                        {field.type === 'SELECT' && (
                          <FormSelect
                            name={field.id}
                            label={field.name}
                            options={(field.options || []).map((opt: string) => ({ value: opt, label: opt }))}
                          />
                        )}
                        {field.type === 'BOOLEAN' && (
                          <FormSwitch
                            name={field.id}
                            label={field.name}
                          />
                        )}
                        {field.type === 'CLOCK' && (
                          <FormTimePicker
                            name={field.id}
                            label={field.name}
                          />
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </FormProvider>
  );
};

export default DayMetricsForm;
